import React, { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@apollo/client';
import * as S from '@/components/Ad/Description/Description.styles';
import { useAdDetails } from '@/views/Ads/useAdDetails';
import { Divider } from '@/components/UI/Divider/Divider.styles';
import { useIsAuthor } from '@/hooks/useIsAuthor';
import { CopyRestrictedText } from '@/components/CopyRestrictedText/CopyRestrictedText';
import { isTextOverflowing } from '@/components/Ad/Description/Description.helpers';
import { SkeletonLoader } from '@/components/SkeletonLoader/SkeletonLoader';
import { GET_USER } from '@/api/queries/user.queries';
import { GetUserResponse } from '@/api/types/user.types';

const ONE_LINE_HEIGHT = 24;
const MAX_LINES = 7;
const VERY_LARGE_LINE_LIMIT = 100000;

export const Description = () => {
  const { t } = useTranslation();
  const { adDetails } = useAdDetails();
  const isAuthor = useIsAuthor();
  const { data: userInfo } = useQuery<GetUserResponse>(GET_USER);
  const ref = useRef(null);
  const [isOpen, setIsOpen] = useState(false);
  const isB2B = userInfo?.currentUser?.isActiveShop;
  const isCopyDisabled = isB2B && !isAuthor;

  if (!adDetails) {
    return (
      <>
        <SkeletonLoader
          dataTestId="ad-description-skeleton-loader"
          borderRadius={7}
          height="168px"
        />
        <Divider $isSmall />
      </>
    );
  }

  if (!adDetails.body) {
    return null;
  }

  const linesCount = isOpen ? VERY_LARGE_LINE_LIMIT : MAX_LINES;
  const canOpen = ref.current && isTextOverflowing(ref.current) && !isOpen;

  function toggle() {
    setIsOpen(true);
  }

  return (
    <>
      <S.TextBox $isOpen={isOpen}>
        <CopyRestrictedText
          disableSelection={isCopyDisabled}
          disableRightClick={isCopyDisabled}
          disableCopyEvent={isCopyDisabled}
        >
          <S.Text
            ref={ref}
            $variant="2"
            $maxHeight={ONE_LINE_HEIGHT * linesCount}
            data-testid="ad-description"
          >
            {adDetails.body}
          </S.Text>
        </CopyRestrictedText>
      </S.TextBox>
      {canOpen && (
        <S.Toggle $variant="2" onClick={toggle}>
          {t('common.read_more')}
        </S.Toggle>
      )}
      <Divider $isSmall />
    </>
  );
};
